import { Spacing } from '@persona-id/design-tokens';
import { StarFillIcon } from '@persona-id/design-tokens/icons';
import { forwardRef, ReactNode, useCallback } from 'react';
import styled, { css } from 'styled-components';

import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import usePlanRequirements from 'dashboard/components/PlanUpgradeFlow/usePlanRequirements';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Z_INDICES } from 'dashboard/styles/variables';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Paragraph, ParagraphProps } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import hoverableMixin from 'design-system/mixins/hoverable';
import { TransitionDuration } from 'design-system/variables/transitions';

type CardSize = 'xs' | 'sm' | 'md';

const StyledCard = styled(Card)<{
  withBorder: boolean;
  justify: 'start' | 'center';
  disabled: boolean;
  selected?: boolean;
}>`
  display: flex;
  ${hoverableMixin}
  &:hover,
  &:active,
  &:focus {
    ${({ selected, disabled }) =>
      !selected &&
      !disabled &&
      css`
        outline: 2px solid ${({ theme }) => theme.color.primary.brand200};
      `}
  }
  transition: transform ${TransitionDuration.Medium};
  margin-bottom: 0px;
  ${({ withBorder }) =>
    withBorder &&
    css`
      outline: 1px solid ${({ theme }) => theme.color.border.primary};
    `}
  ${({ justify }) =>
    justify === 'center' &&
    css`
      justify-content: center;
    `}
  ${({ selected }) =>
    selected &&
    css`
      outline: 1px solid ${({ theme }) => theme.color.primary.brand600};
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color.background.disabled};
      cursor: none;
      user-select: none;
      p,
      label {
        color: ${({ theme }) => theme.color.text.disabled};
      }
      svg {
        opacity: 0.4;
      }
    `}
`;

const StyledCardBody = styled(Card.Body)<{ size: CardSize; justify: 'start' | 'center' }>`
  display: flex;
  flex-direction: row;
  flex: auto;
  padding: ${({ size }) => {
    switch (size) {
      case 'md':
        return Spacing[5];
      case 'sm':
        return Spacing[4];
      case 'xs':
        return Spacing[2];
    }
  }};
  ${({ justify }) =>
    justify === 'center' &&
    css`
      align-items: center;
    `}
`;

const SmallCardTitle = styled(Paragraph).attrs<ParagraphProps>(({ size = 'sm' }) => ({ size }))`
  color: black;
`;

const ModuleIcon = styled.div<{ size: CardSize; rotationDegrees?: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  background: ${({ theme }) => theme.color.primary.brand100};
  transform: rotate(${({ rotationDegrees }) => rotationDegrees ?? 0}deg);
  ${({ size }) => {
    switch (size) {
      case 'md':
        return css`
          height: ${Spacing[7]};
          width: ${Spacing[7]};
          padding: ${Spacing[2]};
        `;
      case 'sm':
        return css`
          height: 36px;
          width: 36px;
          padding: ${Spacing[2]};
        `;
      case 'xs':
      default:
        return css`
          height: ${Spacing[5]};
          width: ${Spacing[5]};
          padding: ${Spacing[1]};
        `;
    }
  }}
  svg {
    width: ${Spacing[5]};
    height: ${Spacing[5]};
    color: ${({ theme }) => theme.color.primary.brand700};
  }
`;

const ContentWrapper = styled.div<{ size: CardSize; justify: 'start' | 'center' }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-left: ${({ size }) => (size === 'xs' ? Spacing[2] : Spacing[3])};
  ${({ justify }) =>
    justify === 'center' &&
    css`
      justify-content: center;
    `}
`;

const HorizontalContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Spacing[1]};
`;

const CardDescription = styled(Paragraph)`
  line-height: 22px;
`;

export interface TemplateModuleCardProps {
  id: string;
  size: CardSize;
  justify?: 'start' | 'center';
  title: string;
  description?: string;
  icon: ReactNode;
  rotationDegrees?: number;
  disabled?: boolean;
  withBorder?: boolean;
  selected?: boolean;
  requiredProductFeatures: ProductFeatureName[];
  requiresOrderForm: boolean;
  onClick: () => void;
}

const TemplateModuleCard = forwardRef<HTMLDivElement, TemplateModuleCardProps>(
  (
    {
      id,
      size,
      justify = 'start',
      title,
      description,
      icon,
      rotationDegrees,
      disabled,
      withBorder,
      selected,
      requiredProductFeatures,
      requiresOrderForm,
      onClick,
    }: TemplateModuleCardProps,
    forwardedRef,
  ) => {
    const { requiresPlanUpgrade, requiresAddOn } = usePlanRequirements(
      requiredProductFeatures,
      requiresOrderForm,
    );

    const handleClick = useCallback(() => {
      if (!disabled) {
        onClick();
      }
    }, [disabled, onClick]);

    return (
      <StyledCard
        ref={forwardedRef}
        withBorder={withBorder}
        disabled={disabled}
        justify={justify}
        selected={selected}
      >
        <StyledCardBody id={id} data-test={id} size={size} justify={justify} onClick={handleClick}>
          <ModuleIcon size={size} rotationDegrees={rotationDegrees}>
            {icon}
          </ModuleIcon>
          <ContentWrapper size={size} justify={justify}>
            {size === 'xs' ? (
              <HorizontalContainer>
                <SmallCardTitle>{title}</SmallCardTitle>
                {requiresAddOn ? (
                  <Sticker icon={<StarFillIcon />} variant='purple'>
                    Add-on
                  </Sticker>
                ) : requiresPlanUpgrade ? (
                  <LimitedBadge id={id} />
                ) : null}
              </HorizontalContainer>
            ) : (
              <>
                <HorizontalContainer style={{ marginBottom: '6px' }}>
                  <Paragraph
                    size={size}
                    weight={['md', 'lg'].includes(size) ? 'bold' : undefined}
                    color='primary'
                  >
                    {title}
                  </Paragraph>
                  {requiresAddOn ? (
                    <Sticker icon={<StarFillIcon />} variant='purple'>
                      Add-on
                    </Sticker>
                  ) : requiresPlanUpgrade ? (
                    <LimitedBadge id={id} />
                  ) : null}
                </HorizontalContainer>
                <CardDescription size={size}>{description}</CardDescription>
              </>
            )}
          </ContentWrapper>
        </StyledCardBody>
      </StyledCard>
    );
  },
);

const LimitedBadge = ({ id }: { id: string }) => {
  return (
    <Tooltip
      side='top'
      // Need a z-index higher than that of modal
      zIndex={Z_INDICES.toast}
      trigger={
        <div>
          <InquiriesGatingSticker
            dataMarketingIdSuffix={id}
            stickerType={EnhancedGatingStickerType.Limited}
            iconOnly={false}
          />
        </div>
      }
    >
      <span style={{ maxWidth: '200px' }}>This feature is not available on your current plan.</span>
    </Tooltip>
  );
};

TemplateModuleCard.displayName = 'TemplateModuleCard';
export default TemplateModuleCard;
